export const toggleArrObj = (arr, item, getValue = item => item) => {
	if (arr.some(i => getValue(i) === getValue(item))) return arr.filter(i => getValue(i) !== getValue(item));
	else return [...arr, item];
};

export const searchInfo = (arr, item, getValue = item => item) => {
	return arr.filter(i => getValue(i) === getValue(item));
};

export const percentPick = ({ teamValue, playerValue, percent = 20, round }) => {
	return +(((teamValue - playerValue) * percent * round) / 100).toFixed(2);
};

// ADP algorithm: adjusts per-team player rankings after a pick.
// Returns { [playerId]: newADP } for every affected player.
//
// Two rules (client-approved):
//   1. Picked player, only if it is a REACH (adp > selection, ranked worse than
//      the pick slot): moves up to the midpoint (selection + adp) / 2. A value
//      pick (adp <= selection) does not move up.
//   2. Every AVAILABLE player ranked HIGHER than the pick (adp < pickedADP)
//      shifts down one spot (+1). This is a discrete shift, NOT a
//      converge-to-a-floor average — so players keep distinct positions and the
//      order stays adjustable. Picking a clustered player pushes everyone above
//      him down 1, so he rises past them (this is what fixes the "log jam").
//
// boardPlayers must each have an `adp` field (or fall back to `ranking`) and
// should be the AVAILABLE pool (players already drafted are off the board).
// The player's rank among the players still AVAILABLE on a team's board — i.e. his BPA at this
// moment. The board is stored in ADP order, so walking it and skipping what has gone gives the
// same number the pick panel shows. Returns null when he is not on that board at all.
export const boardAvailabilityRank = (boardIds, excludedIds, playerId) => {
	let rank = 0;
	for (const id of boardIds ?? []) {
		if (excludedIds.has(id)) continue;
		rank++;
		if (id === playerId) return rank;
	}
	return null;
};

// Is this committed pick off the user's board for the pick being made now?
//  - drafted this iteration by anyone, any round → gone (rule 1)
//  - taken by this team earlier → depends on the challenge. The first round challenge and fanatic
//    mode are "uniqueness": once the user takes a player he never comes back. The second and third
//    round challenges lock away only the user's picks in the LAST round (2 or 3); their picks in
//    the earlier rounds are allowed to repeat across iterations (client, 2026-09-19 and
//    2026-10-17).
export const isOffUserBoard = (p, { iteration, teamName, mode }) => {
	if (p?.player?.id == null) return false;
	if (p.iteration === iteration) return true;
	if (p.round?.name !== teamName) return false;
	return mode === 2 || mode === 3 ? +p.round_index_number === mode : true;
};

// Third round challenge: where each of the user's round-3 picks replays the earlier iterations'
// selections. A pick owns a block of `blockSize` slots (iterations − 1) just before it; iteration
// k's player goes to block[k − 1] and is replayed there in every later iteration.
// Round-3 picks close enough for their blocks to overlap are grouped (client, 2026-10-17): the
// blocks stack in front of the group's earliest pick, the latest pick's block farthest back (the
// first round challenge's pair order). Counting back skips the team's own picks in any round, and
// reaching one of its round-3 picks pulls that pick into the group — Seahawks 100 + 101 share
// 82-99; with a pick at 90 as well, the three would share 63-89.
//   ownPicks — every pick number (index) the team holds in the challenge, any round
//   round3   — the team's round-3 pick numbers
// Returns Map(pick → block): blockSize slots, ascending. A slot is null only where the count ran
// past pick 1, which takes the earliest iterations of the farthest block.
export const thirdRoundReplayBlocks = (ownPicks, round3, blockSize) => {
	const own = new Set(ownPicks.map(Number));
	const pending = [...new Set(round3.map(Number))].sort((a, b) => b - a); // latest first
	const blocks = new Map();
	while (pending.length) {
		const group = [pending.shift()];
		let slots;
		for (;;) {
			const earliest = group[group.length - 1];
			const need = blockSize * group.length;
			slots = [];
			let joined = false;
			for (let s = earliest - 1; s >= 1 && slots.length < need; s--) {
				if (!own.has(s)) slots.push(s);
				else if (s === pending[0]) {
					group.push(pending.shift());
					joined = true;
					break;
				}
			}
			if (!joined) break;
		}
		const need = blockSize * group.length;
		const layout = [...Array(need - slots.length).fill(null), ...slots.sort((a, b) => a - b)];
		group.forEach((pick, i) => blocks.set(pick, layout.slice(i * blockSize, (i + 1) * blockSize)));
	}
	return blocks;
};

export const calculateADP = (selectionNumber, boardPlayers, pickedPlayer) => {
	const getADP = p => p.adp ?? p.ranking ?? 0;
	const pickedADP = getADP(pickedPlayer);

	const result = {};

	// 1. Picked reach moves up to the midpoint between the slot and where he was.
	if (pickedADP > selectionNumber) {
		result[pickedPlayer.id] = (selectionNumber + pickedADP) / 2;
	}

	// 2. Every available player ranked higher than the pick shifts down 1.
	for (const p of boardPlayers) {
		if (p.id === pickedPlayer.id) continue;
		const adp = getADP(p);
		if (adp < pickedADP) {
			result[p.id] = adp + 1;
		}
	}

	return result;
};

export const dataURLtoBlob = dataurl => {
	var arr = dataurl.split(','),
		mime = arr[0].match(/:(.*?);/)[1],
		bstr = atob(arr[1]),
		n = bstr.length,
		u8arr = new Uint8Array(n);

	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	return new Blob([u8arr], { type: mime });
};

let sumNumber = num => {
	let sum = 0;
	for (let i = 1; i <= num; i++) {
		sum += i;
	}
	return sum;
};

export const upUsersCals = (players, pricentValuePlayer, key) => {
	const point = sumNumber(players.length);
	// const point = sumNumber(players.length -1);
	const midPoint = pricentValuePlayer / point;

	return players.map((player, idx) => {
		const pricentValue = +(midPoint * (players.length - idx - 1)).toFixed(2);
		if (idx === players.length - 1) {
			return {
				...player,
				value: +(player[key] + pricentValuePlayer),
				pricentValue: pricentValuePlayer,
			};
		}
		return {
			...player,
			value: +(player[key] - pricentValue) <= 0 ? 0.00001 : -pricentValue,
			pricentValue: +(player[key] - pricentValue) <= 0 ? 0.00001 : -pricentValue,
		};
	});
};

// Round Calc Create New Data
export const roundTeam = (roundCount, teams) => {
	const newData = [];
	let indexCount = 1;
	for (let i = 1; i <= roundCount; ++i) {
		for (let j = 0; j < teams.length; ++j) {
			newData.push({
				id: Math.random(),
				index: indexCount,
				round_index: `${i} Round`,
				round: teams[j],
			});
			indexCount++;
		}
	}
	return newData;
};

export const getFilterTwoData = (arr, secondArr, key, deepKey = '', bitwiseOperator = 'XOR') => {
	if (bitwiseOperator === 'AND') {
		if (deepKey) {
			return arr.filter(item => secondArr.includes(item[key][deepKey]));
		} else return arr.filter(item => secondArr.includes(item[key]));
	} else if (bitwiseOperator === 'XOR') {
		if (deepKey) {
			return arr.filter(item => !secondArr.includes(item[key][deepKey]));
		} else return arr.filter(item => !secondArr.includes(item[key]));
	}
};

export const objectSet = (arr, key) => {
	const mySet = [];
	const setObject = [];
	arr.forEach(item => {
		if (!mySet.includes(item[key])) {
			mySet.push(item[key]);
			setObject.push(item);
		}
	});
	return setObject;
};

// Get Random Team
export const getRandom = (arr, n) => {
	var result = new Array(n),
		len = arr.length,
		taken = new Array(len);
	if (n > len) throw new RangeError('getRandom: more elements taken than available');
	while (n--) {
		var x = Math.floor(Math.random() * len);
		result[n] = arr[x in taken ? taken[x] : x];
		taken[x] = --len in taken ? taken[len] : len;
	}
	return result;
};

export const makeRepeated = (arr, repeats) =>
	Array.from({ length: repeats }, (v, idx) => {
		const arrIteration = arr.map(item => {
			return { ...item, iteration: idx + 1 };
		});
		return arrIteration;
	}).flat();

export const iterationRound = ({ fanaticChallenge, tradeValueData, round }) => {
	let newTradeValueIteration = [];
	let roundStart = [1];
	const iterationSection = [];
	let fanaticSlices = {
		1: [],
		2: [],
		3: [],
	};
	let fanaticSlicesRound = [
		{
			round: 1,
			start: null,
			end: null,
			challenge: false,
			iteration: 5,
			chose: true,
			roundRange: [],
		},
		{
			round: 2,
			start: null,
			end: null,
			challenge: false,
			iteration: 10,
			chose: false,
			roundRange: [],
		},
		{
			round: 3,
			start: null,
			end: null,
			challenge: false,
			iteration: 15,
			chose: false,
			roundRange: [],
		},
	];
	const iterationRound = makeRepeated(tradeValueData, fanaticChallenge[0].iteration);

	newTradeValueIteration.push(...iterationRound);
	for (let i = 1; i <= fanaticChallenge[0].iteration; i++) {
		iterationSection.push(tradeValueData.length * i);
	}

	for (let i = 0; i < tradeValueData.length; i++) {
		if (+tradeValueData[i].round_index_number === 1 && fanaticSlicesRound[0].start === null) {
			fanaticSlicesRound[0].start = i;
			roundStart.push(i + 1);
		}
		if (+tradeValueData[i].round_index_number === 2 && fanaticSlicesRound[1].start === null) {
			fanaticSlicesRound[0].end = i;
			fanaticSlicesRound[1].start = i;
			roundStart.push(i + 1);
		}
		if (+tradeValueData[i].round_index_number === 3 && fanaticSlicesRound[2].start === null) {
			fanaticSlicesRound[2].start = i;
			fanaticSlicesRound[1].end = i;
			roundStart.push(i + 1);
		}
		if (tradeValueData.length - 1 === i) {
			fanaticSlicesRound[round - 1].end = i;
		}
	}

	for (let i = 0; i < fanaticSlicesRound.length; i++) {
		for (let j = 1; j <= fanaticChallenge[0].iteration; j++) {
			if (fanaticSlicesRound[i].roundRange.length === 0) {
				fanaticSlicesRound[i].roundRange.push({
					start: fanaticSlicesRound[i].start + 1 * j,
					end: fanaticSlicesRound[i].end * j,
				});
			} else {
				fanaticSlicesRound[i].roundRange.push({
					start: fanaticSlicesRound[i].roundRange[j - 2].end,
					end: fanaticSlicesRound[i].end * j,
				});
			}
		}
	}

	const cutTradeValue = [...newTradeValueIteration];
	const newTradeValue = cutTradeValue.map((item, idx) => {
		const newItem = structuredClone(item);
		fanaticSlices[`${item.round_index_number}`].push(idx + 1);
		newItem['index_position'] = idx + 1;
		return newItem;
	});
	const sliceRound = fanaticSlicesRound;
	return {
		count: newTradeValue.length,
		newTradeValue,
		roundStart,
		iterationSection,
		fanaticSlicesRound: { sliceRound, iterationSection },
	};
};

export const iterationFanaticMode = ({ fanaticModeValue, tradeValueData }) => {
	const iterationRound = makeRepeated(tradeValueData, fanaticModeValue);
	console.log('🚀 ~ iterationFanaticMode ~ fanaticModeValue:', fanaticModeValue);
	const iterationSection = [];
	for (let i = 1; i <= fanaticModeValue; i++) {
		iterationSection.push(tradeValueData.length * i);
	}
	const newTradeValue = iterationRound.map((item, idx) => {
		const newItem = structuredClone(item);
		newItem['index_position'] = idx + 1;
		return newItem;
	});
	return { newTradeValue, iterationSection };
};

export const objectDeleteValue = ({ objectData, deleteKey }) => {
	const objectDelete = Object.keys(objectData)
		.filter(key => !deleteKey.includes(key))
		.reduce((obj, key) => {
			obj[key] = objectData[key];
			return obj;
		}, {});
	return objectDelete;
};

// sort array
export const sortArray = ({ arr, key }) => {
	return arr.sort((a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0));
};

export const filteredArray = ({ arr, arr2, key }) => {
	const filterArr = arr.filter(function (array_el) {
		return (
			arr2.filter(function (anotherOne_el) {
				return anotherOne_el[key] === array_el[key];
			}).length === 0
		);
	});
	return filterArr;
};

export const loadImage = src => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			ctx.drawImage(img, 0, 0);
			resolve(canvas.toDataURL('image/png'));
		};
		img.onerror = () => {
			reject(new Error('Failed to load image for canvas conversion'));
		};
		img.src = src;
	});
};

export const generateID = function (count = 8) {
	return (Date.now().toString(36) + Math.random().toString(36).substr(2)).toUpperCase().slice(count);
};

export const formatDate = value => {
	const date = new Date(value);
	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	const day = String(date.getUTCDate()).padStart(2, '0');
	const hours = String(date.getUTCHours()).padStart(2, '0');
	const minutes = String(date.getUTCMinutes()).padStart(2, '0');
	return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const getCurrentDate = () => {
	let myDate = new Date();

	// Create an array of month names
	let monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	// Get the components
	let newDayOfWeek = myDate.toLocaleString('en-US', { weekday: 'short' });
	let newMonth = monthNames[myDate.getMonth()];
	let newDay = myDate.getDate();
	let newYear = myDate.getFullYear();
	let newHours = ('0' + myDate.getHours()).slice(-2);
	let newMinutes = ('0' + myDate.getMinutes()).slice(-2);
	return newDayOfWeek + ' ' + newMonth + ' ' + newDay + ' ' + newYear + ' ' + newHours + ':' + newMinutes;
};

export const dateFormat = date => {
	const originalDateString = date;
	const originalDate = new Date(originalDateString);

	// Format the date as YYYY-MM-DD HH:mm:ss
	const formattedDate = originalDate.toISOString().replace(/T/, ' ').replace(/\..+/, '');

	return formattedDate;
};
export const dateFormatNew = date => {
	// Format the date as YYYY-MM-DD HH:mm:ss
	const formattedDate = date.toISOString().replace(/T/, ' ').replace(/\..+/, '');

	return formattedDate;
};
export const getCurrentNewDate = () => {
	const now = new Date();

	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const day = String(now.getDate()).padStart(2, '0');
	const hours = String(now.getHours()).padStart(2, '0');
	const minutes = String(now.getMinutes()).padStart(2, '0');
	const seconds = String(now.getSeconds()).padStart(2, '0');

	const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
	return formattedDate;
};
export const timeDiffCalc = (dateNow, newDate) => {
	let date1 = new Date(dateNow);
	let date2 = new Date(newDate);

	let diffInMilliseconds = date2 - date1;
	let diffMinute = diffInMilliseconds / (1000 * 60);
	if (diffMinute >= 0 && diffMinute <= 10) {
		return { diffMinute, message: 'access' };
	}
	if (diffMinute < 10) {
		return {
			diffMinute: null,
			message: `You logged earlier, please come back at ${dateNow}`,
		};
	}
	if (diffMinute > 0) {
		return { diffMinute: null, message: `Your event time is over, event started ${dateNow}` };
	}
};

export const splitArrayIntoChunks = (array, chunkSize) => {
	const result = [];
	for (let i = 0; i < array.length; i += chunkSize) {
		result.push(array.slice(i, i + chunkSize));
	}
	return result;
};


export const generateRangeArray = (length, index, stepCount = 5) => {
	const rangeArray = [];
	const step = stepCount;

	for (let i = 0; i < length / 2; i += step) {
		const start = i + 1;
		const end = i + step;
		rangeArray.push(`${start}-${end}`);
	}
	return rangeArray[index];
};

export const generateNewRangeArray = (length, index, stepCount = 5) => {
	const rangeArray = [];
	const start = index;
	const end =length;
	rangeArray.push(`${start}-${end}`)
	return  rangeArray[0]
};

//before
// export const rankingDivideDataGraphics = data => {
// 	const maxFirstSubsetLength = 15;// Max length for the first subset
// 	const totalLength = data.length;
//
// 	const remainderLength = totalLength - maxFirstSubsetLength;
// 	const result = [];
// 	const firstSubset = data.slice(0, Math.min(maxFirstSubsetLength, totalLength));
// 	result.push(firstSubset);
//
// 	if (remainderLength > 0) {
// 		const remainder = data.slice(maxFirstSubsetLength);
// 		if (remainderLength >= 30 && remainderLength <= 45) {
// 			const chunkSize = Math.ceil(remainder.length / 3);
// 			for (let i = 0; i < 3; i++) {
// 				result.push(remainder.slice(i * chunkSize, (i + 1) * chunkSize));
// 			}
// 		} else if (remainderLength >= 15 && remainderLength < 30) {
// 			const chunkSize = Math.ceil(remainder.length / 2);
// 			for (let i = 0; i < 2; i++) {
// 				result.push(remainder.slice(i * chunkSize, (i + 1) * chunkSize));
// 			}
// 		}
// 	//added
// 		else if (remainderLength >= 1 && remainderLength < 15) {
// 			const chunkSize = Math.ceil(remainder.length);
// 			for (let i = 0; i < 1; i++) {
// 				result.push(remainder.slice(i * chunkSize, (i + 1) * chunkSize));
// 			}
// 		}
// 	}
// 	return result;
// };



export const rankingDivideDataGraphics = data => {
	const maxFirstSubsetLength = 15;// Max length for the first subset
	const totalLength = data.length;
	const remainderLength = totalLength - maxFirstSubsetLength;
	const result = [];
	const firstSubset = data.slice(0, Math.min(maxFirstSubsetLength, totalLength));
	result.push(firstSubset);
	if (remainderLength > 0) {
		const remainder = data.slice(maxFirstSubsetLength);
		if (remainderLength >= 30 && remainderLength <= 45) {
			const chunkSize = Math.ceil(remainder.length / 3);
			for (let i = 0; i < 3; i++) {
				result.push(remainder.slice(i * chunkSize, (i + 1) * chunkSize));
			}
		} else if (remainderLength >= 15 && remainderLength < 30) {
			const chunkSize = Math.ceil(remainder.length / 2);
			for (let i = 0; i < 2; i++) {
				result.push(remainder.slice(i * chunkSize, (i + 1) * chunkSize));
			}
		}
		else if (remainderLength >= 6 && remainderLength < 15) {
			const chunkSize = Math.ceil(remainder.length);
			for (let i = 0; i < 1; i++) {
				result.push(remainder.slice(i * chunkSize, (i + 1) * chunkSize));
			}
		}
		else if (remainderLength >= 1 && remainderLength <= 5) {
			remainder.unshift(...firstSubset.splice(firstSubset.length - 5));
			result.push(remainder)
		}
		//added before
		// else if (remainderLength >= 1 && remainderLength < 15) {
		// 	const chunkSize = Math.ceil(remainder.length);
		// 	console.log(chunkSize, "chunkSize")
		// 	for (let i = 0; i < 1; i++) {
		// 		result.push(remainder.slice(i * chunkSize, (i + 1) * chunkSize));
		// 	}
		// }
	}
	return result;
};

export function getRankingYear() {
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();
	if (currentMonth >= 4 && currentMonth <= 11) {
		return currentYear + 1;
	} else {
		return currentYear;
	}
}

// export const  timeDiffCalc = (dateNow, newDate) => {
//   let date1 = new Date(dateNow);
//   let date2 = new Date(newDate);

//   let dif = date2 - date1;
//   dif = Math.round(dif / 1000 / 60);
//   return dif
// }
// timeDiffCalc("2023-12-11 12:15:00", "2023-12-11 12:30:00");

export const isPositionUnique = (teamUniqPositionFanaticChallenge, round, iteration, position, name) => {
	const usedPositions = new Set();
	const iterationKey = `iteration-${iteration}`;

	for (let r = 1; r <= round; r++) {
		const roundChallenges = teamUniqPositionFanaticChallenge[name]?.find(ch => ch.round === r);
		if (roundChallenges && roundChallenges[iterationKey]) {
			roundChallenges[iterationKey].forEach(pos => usedPositions.add(pos));
		}
	}

	return !usedPositions.has(position);
};

// Function to filter the general player list
// export const filterPlayers = (
// 	teamDraftPlayerChoose,
// 	infoDraft,
// 	teamId,
// 	round,
// 	iteration,
// 	pick,
// 	generalPlayerList,
// 	fanaticPlayerBefore = [],
// 	myTeam,
// 	teamSelectIndex // Accept teamSelectIndex as a parameter
// ) => {
// 	debugger

// 	let countPlayer = teamSelectIndex <= 5 ? 1 : 2

// 	// Find the team by teamId
// 	const team = teamDraftPlayerChoose.find(team => team.id === teamId)
// 	if (!team) {
// 		return generalPlayerList // Return the original list if team not found
// 	}

// 	// Create a count of each player ID in the choosePlayers array
// 	const chosenPlayerCount = team.choosePlayers.reduce((acc, player) => {
// 		acc[player.id] = (acc[player.id] || 0) + 1
// 		return acc
// 	}, {})

// 	// Identify players chosen in the specified round and iteration
// 	const roundInfo = infoDraft.find(r => r.round === round)
// 	const iterationInfo = roundInfo ? roundInfo.iterations.find(it => it.iteration === iteration) : null
// 	const chosenInIteration = new Set(iterationInfo ? iterationInfo.playersIds.map(player => player.playerId) : [])

// 	// Identify players chosen at least once by the team
// 	const chosenOnce = new Set(team.choosePlayers.map(player => player.id))

// 	// Create a set of player IDs from fanaticPlayerBefore
// 	const fanaticPlayerBeforeIds = new Set(fanaticPlayerBefore.map(fp => fp.player.id))

// 	// Filter the general player list
// 	return generalPlayerList.filter(player => {
// 		const count = chosenPlayerCount[player.id] || 0
// 		const selectedInIteration = chosenInIteration.has(player.id)
// 		const isFanaticBefore = fanaticPlayerBeforeIds.has(player.id)

// 		// Apply specific filtering when teamSelectIndex <= 5 and team.pick === 1
// 		if (teamSelectIndex <= 5 && pick === 1) {
// 			// In this case, remove only players selected in pick 1
// 			return !chosenOnce.has(player.id)
// 		}

// 		// Exception for pick 1: remove any player that is in the team's choosePlayers array
// 		if (pick === 1) {
// 			return !chosenOnce.has(player.id)
// 		}

// 		// Keep players chosen 2 or more times if myTeam is true
// 		if (myTeam && count >= 2) {
// 			return true
// 		}

// 		// Otherwise, keep players who are selected only once by the team,
// 		// not selected in the current iteration by any other team,
// 		// and not present in fanaticPlayerBefore
// 		return count < countPlayer && !selectedInIteration && !isFanaticBefore
// 	})
// }
// Function to filter the general player list
export const filterPlayers = (
	teamDraftPlayerChoose,
	infoDraft,
	teamId,
	round,
	iteration,
	pick,
	generalPlayerList,
	fanaticPlayerBefore = [],
	myTeam,
	teamSelectIndex, // Accept teamSelectIndex as a parameter
	fanaticChallenge
) => {
	// Determine the player count threshold based on teamSelectIndex
	let countPlayer = teamSelectIndex <= 5 ? 1 : 2;
	// Second and third round challenges: the cap is always 2 selections of the same player per team
	// per 5-iteration block, whatever slot the user drafts from (client's "2/5 rule").
	if (fanaticChallenge?.[0]?.mode === 2 || fanaticChallenge?.[0]?.mode === 3) countPlayer = 2;

	// Find the team by teamId
	const team = teamDraftPlayerChoose.find(team => team.id === teamId);
	if (!team) {
		return generalPlayerList; // Return the original list if team not found
	}

	// The "no team takes the same player more than twice" cap is 2 selections per team across a
	// block of 5 iterations, counting both rounds together (client: "twice across all iterations,
	// regardless of round"). The second round challenge runs 10 iterations, so iterations 6-10
	// start from a clean count and replay the same simulator behaviour as 1-5 (client rule: "for
	// iteration 6, reset the logic for 6-10"). Picks saved without an iteration tag fall through
	// as before, so a 5-iteration challenge is unaffected.
	const ITERATION_BLOCK = 5;
	const currentBlock = Math.ceil((+iteration || 1) / ITERATION_BLOCK);
	const blockPlayers = team.choosePlayers.filter(
		player => player._iteration == null || Math.ceil(+player._iteration / ITERATION_BLOCK) === currentBlock
	);

	// Create a count of each player ID in the choosePlayers array
	const chosenPlayerCount = blockPlayers.reduce((acc, player) => {
		acc[player.id] = (acc[player.id] || 0) + 1;
		return acc;
	}, {});

	// Identify players chosen in the specified round and iteration
	const roundInfo = infoDraft.find(r => r.round === round);
	const iterationInfo = roundInfo ? roundInfo.iterations.find(it => it.iteration === iteration) : null;
	const chosenInIteration = new Set(iterationInfo ? iterationInfo.playersIds.map(player => player.playerId) : []);

	// Identify players chosen at least once by the team (same 5-iteration block)
	const chosenOnce = new Set(blockPlayers.map(player => player.id));

	// Create a set of player IDs from fanaticPlayerBefore
	const fanaticPlayerBeforeIds = new Set(fanaticPlayerBefore.map(fp => fp.player.id));

	// Filter the general player list
	return generalPlayerList.filter(player => {
		const count = chosenPlayerCount[player.id] || 0;
		const selectedInIteration = chosenInIteration.has(player.id);
		const isFanaticBefore = fanaticPlayerBeforeIds.has(player.id);
		// debugger; me
		// Apply specific filtering when teamSelectIndex <= 5 and fanaticChallenge mode is 1
		if (teamSelectIndex <= 5 && fanaticChallenge.length !== 0 && fanaticChallenge[0].mode === 1) {
			if (myTeam) {
				// User's team: strictly no re-picking across iterations
				return !chosenOnce.has(player.id);
			}
			// AI teams: allowed to pick same player in at most 2 iterations, then must pick next best
			return count < 2 && !selectedInIteration && !isFanaticBefore;
		}

		// Second / third round challenge, user's team, round 1: any available player is fair game
		// EXCEPT the ones taken in the last round (2 or 3) of earlier iterations — they are held for
		// the replay slots (client: uniqueness applies to the first round challenge and fanatic mode,
		// NOT to the 2nd/3rd round challenge's earlier rounds).
		if (fanaticChallenge.length !== 0 && (fanaticChallenge[0].mode === 2 || fanaticChallenge[0].mode === 3) && myTeam && round === 1) {
			return !isFanaticBefore;
		}

		// Exception for pick 1 of round 1: remove any player that is in the team's choosePlayers array.
		// isFanaticBefore is excluded here too — every other branch already does it. Without it the
		// 1.1 team could draft a player being held for one of the user's forced slots, and the forced
		// pick would then re-draft him later in the same iteration.
		if (pick === 1 && +round === 1) {
			return !chosenOnce.has(player.id) && !isFanaticBefore;
		}

		// Keep players chosen 2 or more times if myTeam is true
		if (myTeam && count >= 2) {
			return true;
		}

		// When teamSelectIndex > 5, remove players chosen more than twice
		// Otherwise, keep players who are selected less than countPlayer by the team,
		// not selected in the current iteration by any other team,
		// and not present in fanaticPlayerBefore
		return count < countPlayer && !selectedInIteration && !isFanaticBefore;
	});
};
export function findTeamIndex(data, team) {
	const teamId = team[0].id; // Store the team id to avoid repeated access

	for (let i = 0; i < data.length; i++) {
		const currentTeam = data[i]; // Store the current data item to avoid repeated access

		if (currentTeam.id === teamId) {
			return i; // Return the index of the matching team
		}
	}

	return -1; // Return -1 if no match is found
}

export function capitalizeWords(str) {
	return str
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
}
